// API

import { AsifPipelineStatus } from '../modules/projects/projects-api-models';
import { ConfigurableField } from './observations.model';
import { PpeDetectionStatus } from '../services/PPE/ppe.models';

/**
 * Represents a Tag object.
 *
 * @interface Tag
 */
export interface Tag {
  tag_id: string;
  project_id: string;
  tag_details: TagDetails;
}

export interface TagDetails {
  id: string;
  hashtag: string;
}

export const AREA_UNIT_OPTIONS = [
  { value: 1, label: 'M' },
  { value: 2, label: 'FT' },
  { value: 3, label: 'IN' },
];

export function getAreaUnitLabel(code: number | null | undefined): string | null {
  if (code == null) {
    return null;
  }
  return AREA_UNIT_OPTIONS.find((o) => o.value === code)?.label ?? null;
}

export interface ProjectDetails {
  configurable_fields?: ConfigurableField[];
  point_cloud_enabled?: number;
  floors_count?: number;
  observations_count?: number;
  id?: string;
  bim_files?: BimFilesObj;
  asif_selected?: number;
  asif_pipeline_status?: AsifPipelineStatus;
  Last_report_id?: string;
  last_point_image?: string;
  default_point_id?: string;
  created_at?: string;
  created_by?: string;
  floor_count?: number;
  floors?: Floor[] | any[];
  folder_id?: string;
  folder_name?: string;
  location_address?: string;
  location_lat?: string;
  location_lng?: string;
  permissions?: string[];
  project_logo?: string;
  project_name?: string;
  project_name_en?: string;
  organization_settings?: {
    toggle_watermark: string;
    toggle_relocating_floor_point: string;
    toggle_blur_faces: string;
  };
  organization_features?: {
    enable_measurements: number;
    is_bim_feature_enabled: boolean;
  };
  reports?: Report[];
  reports_count?: number;
  total_area_space?: number;
  total_area_unit?: number | null;
  project_manager_email?: string;
  project_manager?: string;
  project_description?: string;
  pm_assign_at?: Date;
  project_manager_id?: string;
  project_manager_status?: string;
  is_creator?: string;
  is_project_manager?: boolean;
  is_cloned?: boolean;
  is_clone_source?: boolean;
  sync_status?: 'active' | 'inactive' | null;
  // PC-2348: per-client (KAFD) feature flags, resolved from the project OWNER's client.
  split_screen_capture_time_enabled?: boolean;
  report_manual_ordering_enabled?: boolean;
  tags?: Tag[];
  is_archived?: number | boolean;
  total_cost?: number;
  user_project_role?: string;
  children_projects_count?: number;
  level?: number;
  parent?: ProjectParentObj;
  blur_human_faces_enabled?: number;
  ppe_detection_enabled?: number;
  start_date?: string;
  end_date?: string;
  consultant_image?: string;
  contractor_image?: string;
  supervision_image?: string;
  capacity?: string;
  pmc?: string;
  contractor?: string;
  sub_contractor?: string;
  contract_number?: string;
  engineer?: string;
  autodesk_account?: {
    token: string;
    autodesk_account_id: string;
    autodesk_container_id: string;
    autodesk_root_folder_id: string;
    autodesk_hub_id: string;
    autodesk_project_id: string;
    autodesk_project_name: string;
  };
  // Unifier Integration Fields
  project_scope?: string;
  cluster?: string;
  region?: string;
  latitude?: number;
  longitude?: number;
  project_budget_sar?: number;
  p6_project_id?: string;
  project_status?: string;
  // Unifier API Response Fields (with unifier_ prefix)
  unifier_project_scope?: string;
  unifier_cluster?: string;
  unifier_region?: string;
  unifier_project_admin?: string;
  unifier_latitude?: number;
  unifier_longitude?: number;
  unifier_project_budget_sar?: number;
  unifier_p6_project_id?: string;
  unifier_project_status?: string;
  unifier_start_date?: string;
  unifier_end_date?: string;
  unifier_is_synced?: boolean;
  // Whether NWC sync can actually run for this project: it needs a project number, and only
  // Unifier issues those. A project created by hand in WhiteHelmet has unifier_is_synced but no
  // number, and syncing it always fails NWC's validation (PC-2377).
  unifier_is_linked?: boolean;
  unifier_ref_number?: string;
  unifier_project_id?: string;
  unifier_created_at?: string;
  unifier_updated_at?: string;
  unifier_data?: UnifierExtraData;

  // P6 Integration Fields - PC-975
  project_phase?: string; // Unifier
  finish_date?: string; // P6 - Finish Date
  baseline_start_date?: string; // P6 - BL Start Date
  baseline_end_date?: string; // P6 - BL End Date
  schedule_percent_complete?: string | number; // P6 - Schedule Percent Complete
  completion_percentage?: string | number; // P6 - Actual Percent Complete
  planned_total_cost?: string | number; // P6 - Planned Total Cost
  actual_cost?: string | number; // P6 - Actual Cost
  cost_percentage_completion?: string | number; // P6 - Cost % Complete
  planned_value_cost?: string | number; // P6 - Planned Value Cost
  baseline_total_cost?: string | number; // P6 - BL Total Cost
  evm_value_cost?: string | number; // P6 - Earned Value Cost
  value_of_remaining_work_with_VAT?: string | number; // P6 - Value of Remaining Work with VAT

  // NWC Integration - shared project indicator
  shared_by?: string; // e.g., 'nwc' when project is shared by NWC

  v2_project_id?: string;
  key?: string;
}

export interface UnifierExtraData {
  // Project classification and categorization
  project_type?: string; // Project type (Water, etc.)
  project_category?: string; // Project category (Design & Build, etc.)
  project_type_ar?: string; // Project type in Arabic
  project_classification?: string; // Project classification in Arabic
  project_classification_en?: string; // Project classification in English

  // Organizational information
  operating_unit?: string; // Operating unit
  country?: string; // Country
  annual_capital_plan_period?: string; // Annual capital plan period

  // Project structure and templates
  shell_templates?: string; // Template used for the project
  shell_location?: string; // Shell location path
  created_shell?: string; // Created shell name

  // Project indicators and flags
  pso?: string; // PSO indicator (Yes/No)
  is_business_case_created?: boolean; // Business case creation status

  // Financial information
  project_budget_type?: string; // Type of project budget
  estimated_project_cost?: number; // Estimated project cost
  ebs_po_number?: string; // EBS PO Number

  // Contract and operational information
  operational_contract_number?: string; // Operational contract number
  acppk?: string; // ACPPK field

  // Integration and reporting
  whitehelmet_report_link?: string; // Link to WhiteHelmet report

  // Creator information
  creator?: string; // Creator name

  contract_status?: string; // Contract status
}

export interface Report {
  id?: string;
  /** PPE auto-detection (V2CORE-2901) display status; absent on an older backend. */
  ppe_detection_status?: PpeDetectionStatus | null;
  creator?: string;
  published_at?: string;
  merged_at?: string;
  short_id?: string;
  selected?: boolean;
  floors?: any;
  published_at_timestamp?: number;
  published_by?: string;
  report_name?: string;
  report_type?: string;
  is_merged?: boolean;
  report_floors?: any[];
  pointsOnFloor?: boolean;
  /**
   * Split-screen green dot: set in `NavigationComponent.refreshSplitReportPointCaptureFlags` when
   * `pointsOnFloor` is true and the point APIs list this report with a positive flag — images:
   * `floors/point/:id/reports-having-point-images` (`has_image` / `hasImage`); videos:
   * `floors/point/:id/reports-having-point-videos` (`has_matching_point` / `hasMatchingPoint`, same
   * omitted/false semantics as image rows).
   */
  hasImageForSelectedPoint?: boolean;
}

export interface TileLevel {
  level: number;
  cols_per_face: number;
  rows_per_face: number;
}

export interface PointImage {
  id?: string;
  heading_angle: number;
  report_id?: string;
  floor_id?: string;
  point_id?: string;
  point_name?: string;
  point_x?: number;
  point_y?: number;
  bim_point_x?: string;
  bim_point_y?: string;
  bim_point_z?: string;
  image_360?: string;
  blurred_image?: string;
  image_360_thumb?: string;
  navigation_hotspots: NavigationHotspot[];
  report_floor_id?: string;
  numbers_of_pending_observation?: number;
  numbers_of_in_progress_observation?: number;
  numbers_of_completed_observation?: number;
  capture_time_stamp?: number;
  lat?: number | string;
  lng?: number | string;
  matching_points?: MatchingPoint[];
  vertical_angle?: number;
  bim_offset?: number;
  default_bim_offset?: number;
  horizontal_angle?: number;
  blurred_faces: BlurredFace[];
  height?: number;
  width?: number;
  is_selected_point: number | null;
  map_point_radius: number | null;
  tiles?: TileLevel[];
  blurred_tiles?: TileLevel[];
}

export interface BlurredFace {
  face_id: string;
  x11: number;
  x12: number;
  x21: number;
  x22: number;
  y11: number;
  y12: number;
  y21: number;
  y22: number;
}

export interface MatchingPoint {
  match_report_id: string;
  current_point_uuid: string;
  angle_offset: number;
  match_point_uuid: string;
  id: string;
  report_id: string;
}

export interface NavigationHotspot {
  ath: string;
  atv: string;
  current_point_uuid: string;
  id: string;
  next_point_uuid: string;
  report_id: string;
}

export interface Floor {
  rotation_angle_degrees: string;
  id: string;
  created_at: string;
  created_at_timestamp: number;
  floor_image: string;
  floor_image_hq: string;
  floor_image_original: string;
  floor_name: string;
  floor_points: ProjectFloorPoint[];
  image_height: string;
  image_width: string;
  points_count: number;
  sort: number;
  status:
    | 'FAILED'
    | 'UNDER_REVIEW_BY_AI'
    | 'READY_FOR_OPERATION_ENG'
    | 'PUBLISHED_BY_OPERATION_ENG';
  is_observation_creation_enabled?: boolean;
  is_synced?: boolean;
  original_floor_id?: string | null;
  is_deleted?: boolean;
  is_deleted_in_original?: boolean;
}

interface FloorPoint {
  id: string;
  floor_id: string;
  point_name: string;
  point_x: number;
  point_y: number;
  bim_point_x: number | null;
  bim_point_y: number | null;
  bim_point_z: number | null;
  lat: string | null;
  lng: string | null;
  is_default: boolean;
  is_selected_point: number | null;
  map_point_radius: number | null;
  is_synced?: boolean;
  original_point_id?: string | null;
}

export interface PointLogCauser {
  name: string;
  email: string;
}

export interface PointLogProperties {
  old: {
    point_x: number;
    point_y: number;
  };
  title_ar: string;
  title_en: string;
  attributes: {
    point_x: number;
    point_y: number;
  };
  action?: string;
  'Updated by'?: string;
}

export interface PointLog {
  causer: PointLogCauser;
  action: string;
  date: string;
  properties: PointLogProperties;
}

export interface FloorDetailsResponse {
  floor_details: FloorDetails;
}

export interface FloorDetails {
  id: string;
  floor_name: string;
  floor_image: string;
  floor_image_original: string | null;
  floor_image_hq: string | null;
  size_ratio: number | null;
  image_width: string;
  image_height: string;
  sort: number;
  project_id: string;
  points: FloorPoint[];
  logs: PointLog[];
}

export interface BIMPoints {
  point_name: string;
  floor_map_point_x: number;
  floor_map_point_y: number;
  bim_point_x: number;
  bim_point_y: number;
  bim_point_z: number;
}

export interface BIMPointResponse {
  project_id: string;
  floor_id: string;
  points: BIMPoints[];
}

export interface ObservationLog {
  log_name: string;
  log_name_slug: string;
  log_description: string;
  properties: any;
  created_at: string;
  by: string;
}

export interface Properties {
  old: any;
  attributes: any;
  /** Localized "old" values when userLang is 'ar' */
  old_ar?: Record<string, unknown>;
  /** Localized "old" values when userLang is 'ru' */
  old_ru?: Record<string, unknown>;
  /** Localized "attributes" when userLang is 'ar' */
  attributes_ar?: Record<string, unknown>;
  /** Localized "attributes" when userLang is 'ru' */
  attributes_ru?: Record<string, unknown>;
}

export interface Observation {
  categories?: {
    category_id: string;
    category_name_ar: string;
    category_name_en: string;
  }[];
  count_of_attached?: number;
  created_at?: string;
  creator?: Creator;
  description?: string;
  due_date?: string;
  deleted_at?: string;
  floor?: { floor_id: string; floor_image: string; floor_name: string };
  id?: string;
  location_x?: number;
  location_y?: number;
  point?: { point_id: string; point_name: string };
  significance?: string;
  privacy?: string;
  report?: { report_id: string; report_name: string };
  screen_shot?: string;
  status?: string;
  observation_flag?: string;
  title?: string;
  type?: string;
  unique_id?: string;

  // added
  information?: string;
}

export interface Creator {
  uuid: string;
  name: string;
  status: string;
}

// Project
export interface ProjectInfo {
  isPointCloudEnabled: number;
  floorsCount: number;
  observations_count: number;
  organization_features: {
    enable_measurements: number;
    is_bim_feature_enabled: boolean;
  };
  asif_selected: number;
  asif_pipeline_status: AsifPipelineStatus;
  lastReportId: string;
  bim_files: BimFilesObj;
  last_point_image: string;
  default_point: string;
  createdAt: string;
  createdBy: string;
  folderId: string;
  id: string;
  isCreator: any;
  locationAddress: string;
  locationLat: string;
  locationLng: string;
  projectLogo: string;
  projectName: string;
  projectNameEn: string;
  organizationSettings: {
    toggle_watermark: string;
    toggle_relocating_floor_point: string;
    toggle_blur_faces: string;
  };
  totalAreaSpace: number;
  totalAreaUnit?: number | null;
  reportsCount: number;
  floorCount: number;
  projectManagerEmail: string;
  projectManager: string;
  projectManagerStatus: string;
  projectDescription: string;
  projectManagerAssignedAt: Date;
  projectManagerId: string;
  isProjectManager?: boolean;
  is_cloned?: boolean;
  is_clone_source?: boolean;
  sync_status?: 'active' | 'inactive' | null;
  tags?: Tag[];
  is_archived?: number | boolean;
  permissions?: string[];
  user_project_role?: string;
  totalCost?: number;
  childrenProjectsCount?: number;
  level?: number;
  parent?: ProjectParentObj;
  blurFacePL?: number;
  ppeAIDetectionPL?: number;
  start_date?: string;
  end_date?: string;
  consultant_image?: string;
  contractor_image?: string;
  supervision_image?: string;
  capacity?: string;
  pmc?: string;
  contractor?: string;
  sub_contractor?: string;
  contract_number?: string;
  engineer?: string;
  autodesk_account?: {
    token: string;
    autodesk_account_id: string;
    autodesk_container_id: string;
    autodesk_root_folder_id: string;
    autodesk_hub_id: string;
    autodesk_project_id: string;
    autodesk_project_name: string;
  };
  // Unifier Integration Fields
  project_scope?: string;
  cluster?: string;
  region?: string;
  latitude?: number;
  longitude?: number;
  project_budget_sar?: number;
  p6_project_id?: string;
  project_status?: string;
  // Unifier API Response Fields (with unifier_ prefix)
  unifier_project_scope?: string;
  unifier_cluster?: string;
  unifier_region?: string;
  unifier_project_admin?: string;
  unifier_latitude?: number;
  unifier_longitude?: number;
  unifier_project_budget_sar?: number;
  unifier_p6_project_id?: string;
  unifier_project_status?: string;
  unifier_start_date?: string;
  unifier_end_date?: string;
  unifier_is_synced?: boolean;
  // See ProjectDetails.unifier_is_linked (PC-2377).
  unifier_is_linked?: boolean;
  unifier_ref_number?: string;
  unifier_project_id?: string;
  unifier_created_at?: string;
  unifier_updated_at?: string;
  unifier_data?: UnifierExtraData;
  project_number?: string;

  // P6 Integration Fields - PC-975
  project_phase?: string; // Unifier
  finish_date?: string; // P6 - Finish Date
  baseline_start_date?: string; // P6 - BL Start Date
  baseline_end_date?: string; // P6 - BL End Date
  schedule_percent_complete?: string | number; // P6 - Schedule Percent Complete
  completion_percentage?: string | number; // P6 - Actual Percent Complete
  planned_total_cost?: string | number; // P6 - Planned Total Cost
  actual_cost?: string | number; // P6 - Actual Cost
  cost_percentage_completion?: string | number; // P6 - Cost % Complete
  planned_value_cost?: string | number; // P6 - Planned Value Cost
  baseline_total_cost?: string | number; // P6 - BL Total Cost
  evm_value_cost?: string | number; // P6 - Earned Value Cost
  value_of_remaining_work_with_VAT?: string | number; // P6 - Value of Remaining Work with VAT

  // NWC Integration - shared project indicator
  shared_by?: string; // e.g., 'nwc' when project is shared by NWC

  v2_project_id?: string;
  key?: string;
}

export interface ProjectParentObj {
  id: string;
  parent_id: string | null;
  project_name: string;
  project_name_en: string;
  parent: ProjectParentObj | null;
}

export interface MultiImageReports {
  id: string;
  name: string;
  date: string;
}

export interface ProjectFloorPoint {
  // MapPoint ProjectFloorPoint
  is_default?: boolean;
  id: string;
  lat?: string | number;
  lng?: string | number;
  point_name?: string;
  point_x?: number;
  point_y?: number;
  bim_point_x?: number;
  bim_point_y?: number;
  bim_point_z?: number;
  bim_offset?: number;
  is_relocate_point?: boolean;
  is_selected_point?: number | null;
  map_point_radius?: number | null;
  image?: string;
  is_synced?: boolean;
  original_point_id?: string | null;
  is_deleted?: boolean;
  is_deleted_in_original?: boolean;
}

interface File {
  id: string;
  project_id: string;
  local_path: string;
  urn: string;
  name: string;
  deleted_at: string | null;
  created_at: string;
  updated_at: string;
}

interface Log {
  log_name: string;
  description: string;
  properties: {
    urn: string;
    path: string;
    file_name: string;
    project_id: string;
    project_name: string;
    project_name_en: string;
  };
  created_at: string;
  causer: {
    id: number;
    name: string;
    email: string;
  };
}

export interface BimFilesObj {
  files: File[];
  logs: Log[];
}

// new
/*export interface CurrentLayer {
  floor: any;
  report: any;
  point: any;
  currentObservation: any;
  splittedReport?: any;
  autotour: any;
}*/

export interface ActiveControls {
  map?: boolean;
  report?: boolean;
  floor?: boolean;
  points?: boolean;
  observation?: boolean;
  measure?: boolean;
  split?: boolean;
  splitBim?: boolean;
  lockScreen?: boolean;
  menu?: boolean;
  autoTour?: boolean;
  fullScreen?: boolean;
  imageSettings?: boolean;
}
